/**
 * LegalTemplates PDF Editor — self-signature
 *
 * Signing your own document is free: draw it, type it in a script face, or
 * upload an image. The result is a transparent PNG handed to the image tool,
 * so placing, moving and resizing reuse the image annotation as-is and it
 * ends up in the saved PDF like any image. The last signature can be kept
 * on this device (localStorage) for the next document. Requesting other
 * people's signatures stays the e-sign product (Export → Request signatures).
 */
import { state } from './context.js';
import { activateTool, setStatus, showAlert } from './ui.js';

const SAVED_SIGNATURE_KEY = 'pdfEditorSignature';
const SIGNATURE_FONTS = ['Dancing Script', 'Great Vibes', 'Pacifico'];
const PAD_LINE_WIDTH = 2.6;

const els = {};
const pad = {
    drawing: false,
    strokes: [],
    current: null,
};
const typed = {
    font: SIGNATURE_FONTS[0],
};

function q(id) {
    return document.getElementById(id);
}

function padContext() {
    return els.canvas.getContext('2d');
}

function padPoint(event) {
    const rect = els.canvas.getBoundingClientRect();
    const scaleX = els.canvas.width / Math.max(1, rect.width);
    const scaleY = els.canvas.height / Math.max(1, rect.height);
    return {
        x: (event.clientX - rect.left) * scaleX,
        y: (event.clientY - rect.top) * scaleY,
    };
}

function redrawPad() {
    const context = padContext();
    context.clearRect(0, 0, els.canvas.width, els.canvas.height);
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.strokeStyle = '#111827';
    context.lineWidth = PAD_LINE_WIDTH * (els.canvas.width / 480);
    pad.strokes.concat(pad.current ? [pad.current] : []).forEach((stroke) => {
        if (stroke.length < 2) {
            const point = stroke[0];
            if (point) {
                context.beginPath();
                context.arc(point.x, point.y, context.lineWidth / 2, 0, Math.PI * 2);
                context.fillStyle = context.strokeStyle;
                context.fill();
            }
            return;
        }
        context.beginPath();
        context.moveTo(stroke[0].x, stroke[0].y);
        for (let index = 1; index < stroke.length; index++) {
            const previous = stroke[index - 1];
            const point = stroke[index];
            const midX = (previous.x + point.x) / 2;
            const midY = (previous.y + point.y) / 2;
            context.quadraticCurveTo(previous.x, previous.y, midX, midY);
        }
        context.stroke();
    });
    syncFeedback();
}

function clearPad() {
    pad.strokes = [];
    pad.current = null;
    redrawPad();
}

function trimCanvas(source) {
    const context = source.getContext('2d');
    const { width, height } = source;
    const data = context.getImageData(0, 0, width, height).data;
    let minX = width;
    let minY = height;
    let maxX = -1;
    let maxY = -1;
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            if (data[((y * width) + x) * 4 + 3] > 8) {
                if (x < minX) minX = x;
                if (x > maxX) maxX = x;
                if (y < minY) minY = y;
                if (y > maxY) maxY = y;
            }
        }
    }
    if (maxX < 0) {
        return null;
    }
    const pad = Math.round(Math.max(6, width * 0.012));
    minX = Math.max(0, minX - pad);
    minY = Math.max(0, minY - pad);
    maxX = Math.min(width - 1, maxX + pad);
    maxY = Math.min(height - 1, maxY + pad);
    const out = document.createElement('canvas');
    out.width = maxX - minX + 1;
    out.height = maxY - minY + 1;
    out.getContext('2d').drawImage(source, minX, minY, out.width, out.height, 0, 0, out.width, out.height);
    return out;
}

function currentTab() {
    const active = els.tabs.find((tab) => tab.classList.contains('active'));
    return active ? active.dataset.signTab : 'draw';
}

function setTab(name) {
    els.tabs.forEach((tab) => {
        const isActive = tab.dataset.signTab === name;
        tab.classList.toggle('active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
    els.panes.forEach((pane) => {
        pane.hidden = pane.dataset.signPane !== name;
    });
    if (name === 'draw') {
        window.requestAnimationFrame(redrawPad);
    }
    if (name === 'type' && els.text) {
        els.text.focus();
        renderTypedPreview();
    }
    syncFeedback();
}

function renderTypedPreview() {
    if (!els.typePreview) {
        return;
    }
    const text = String(els.text.value || '').trim();
    els.typePreview.textContent = text || 'Your name';
    els.typePreview.style.fontFamily = `"${typed.font}", cursive`;
    els.typePreview.classList.toggle('is-empty', !text);
    els.fontButtons.forEach((button) => {
        button.classList.toggle('active', button.dataset.signFont === typed.font);
        button.setAttribute('aria-checked', button.dataset.signFont === typed.font ? 'true' : 'false');
    });
    syncFeedback();
}

async function renderTypedSignature(text) {
    const family = typed.font;
    try {
        await document.fonts.load(`80px "${family}"`);
    } catch (error) {
        // The generic cursive fallback still renders.
    }
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    context.font = `80px "${family}", cursive`;
    const metrics = context.measureText(text);
    const width = Math.ceil(metrics.width + 60);
    const height = 140;
    canvas.width = width;
    canvas.height = height;
    context.font = `80px "${family}", cursive`;
    context.fillStyle = '#111827';
    context.textBaseline = 'middle';
    context.fillText(text, 30, height / 2);
    return trimCanvas(canvas) || canvas;
}

function readSavedSignature() {
    try {
        const raw = window.localStorage.getItem(SAVED_SIGNATURE_KEY);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed.dataUrl === 'string' && parsed.dataUrl.startsWith('data:image/') ? parsed : null;
    } catch (error) {
        return null;
    }
}

function writeSavedSignature(signature) {
    try {
        if (signature.dataUrl.length > 400000) {
            return;
        }
        window.localStorage.setItem(SAVED_SIGNATURE_KEY, JSON.stringify(signature));
    } catch (error) {
        // Storage full or blocked: the signature still places fine.
    }
}

function syncSavedTab() {
    const saved = readSavedSignature();
    if (els.savedTab) {
        els.savedTab.hidden = !saved;
    }
    if (els.savedPreview) {
        els.savedPreview.src = saved ? saved.dataUrl : '';
    }
}

function syncFeedback() {
    if (!els.feedback) {
        return;
    }
    const tab = currentTab();
    let ready = false;
    let message = 'Draw, type or upload your signature, then place it on the page.';
    if (tab === 'draw') {
        ready = pad.strokes.length > 0;
        message = ready ? 'Looks good. Place it on the page.' : 'Sign inside the box with your finger, stylus or mouse.';
    } else if (tab === 'type') {
        ready = !!(els.text && els.text.value.trim());
        message = ready ? 'Pick a style, then place it on the page.' : 'Type your name to see it as a signature.';
    } else if (tab === 'upload') {
        ready = !!(els.uploadPreview && els.uploadPreview.dataset.dataUrl);
        message = ready ? 'Place it on the page.' : 'Choose a PNG or JPG of your signature. Transparent PNGs look best.';
    } else if (tab === 'saved') {
        ready = !!readSavedSignature();
        message = ready ? 'Your saved signature. Place it on the page.' : 'No saved signature yet.';
    }
    els.feedback.textContent = message;
    if (els.useButton) {
        els.useButton.disabled = !ready;
    }
}

async function buildSignature() {
    const tab = currentTab();
    if (tab === 'draw') {
        const trimmed = trimCanvas(els.canvas);
        if (!trimmed) {
            return null;
        }
        return { dataUrl: trimmed.toDataURL('image/png'), mimeType: 'image/png', aspectRatio: trimmed.width / trimmed.height };
    }
    if (tab === 'type') {
        const text = String(els.text.value || '').trim();
        if (!text) {
            return null;
        }
        const canvas = await renderTypedSignature(text);
        return { dataUrl: canvas.toDataURL('image/png'), mimeType: 'image/png', aspectRatio: canvas.width / canvas.height };
    }
    if (tab === 'upload') {
        const dataUrl = els.uploadPreview.dataset.dataUrl;
        if (!dataUrl) {
            return null;
        }
        return {
            dataUrl,
            mimeType: dataUrl.startsWith('data:image/jpeg') ? 'image/jpeg' : 'image/png',
            aspectRatio: Number(els.uploadPreview.dataset.aspectRatio) || 3,
        };
    }
    return readSavedSignature();
}

export function openSignatureModal() {
    if (!els.modal) {
        return;
    }
    if (!state.pdfJsDoc) {
        showAlert('Upload a PDF first.', true);
        return;
    }
    syncSavedTab();
    els.modal.classList.add('active');
    els.modal.setAttribute('aria-hidden', 'false');
    setTab(readSavedSignature() ? 'saved' : 'draw');
    document.addEventListener('keydown', onKeydown, true);
}

export function closeSignatureModal() {
    if (!els.modal || !els.modal.classList.contains('active')) {
        return;
    }
    els.modal.classList.remove('active');
    els.modal.setAttribute('aria-hidden', 'true');
    document.removeEventListener('keydown', onKeydown, true);
}

function onKeydown(event) {
    if (event.key === 'Escape') {
        event.stopPropagation();
        event.preventDefault();
        closeSignatureModal();
    }
}

async function useSignature() {
    const signature = await buildSignature();
    if (!signature) {
        syncFeedback();
        return;
    }
    if (els.remember && els.remember.checked) {
        writeSavedSignature(signature);
    }
    state.pendingImage = {
        dataUrl: signature.dataUrl,
        mimeType: signature.mimeType,
        aspectRatio: Math.max(0.2, Number(signature.aspectRatio) || 3),
    };
    state.userChoseTool = true;
    closeSignatureModal();
    activateTool('image');
    setStatus('Click where the signature goes');
    showAlert('Click the spot on the page where your signature goes. Drag to move, use the corner to resize.');
}

function bindPad() {
    const canvas = els.canvas;
    canvas.addEventListener('pointerdown', (event) => {
        event.preventDefault();
        canvas.setPointerCapture(event.pointerId);
        pad.drawing = true;
        pad.current = [padPoint(event)];
        redrawPad();
    });
    canvas.addEventListener('pointermove', (event) => {
        if (!pad.drawing || !pad.current) {
            return;
        }
        event.preventDefault();
        pad.current.push(padPoint(event));
        redrawPad();
    });
    const finish = () => {
        if (!pad.drawing) {
            return;
        }
        pad.drawing = false;
        if (pad.current && pad.current.length) {
            pad.strokes.push(pad.current);
        }
        pad.current = null;
        redrawPad();
    };
    canvas.addEventListener('pointerup', finish);
    canvas.addEventListener('pointercancel', finish);
    canvas.addEventListener('pointerleave', finish);
}

function bindUpload() {
    els.file.addEventListener('change', () => {
        const file = els.file.files && els.file.files[0];
        if (!file) {
            return;
        }
        if (!/^image\/(png|jpeg)$/i.test(file.type)) {
            showAlert('Choose a PNG or JPG image.', true);
            els.file.value = '';
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            const dataUrl = String(reader.result || '');
            const image = new Image();
            image.onload = () => {
                els.uploadPreview.src = dataUrl;
                els.uploadPreview.hidden = false;
                els.uploadPreview.dataset.dataUrl = dataUrl;
                els.uploadPreview.dataset.aspectRatio = String(image.width / Math.max(1, image.height));
                syncFeedback();
            };
            image.src = dataUrl;
        };
        reader.readAsDataURL(file);
    });
}

export function bindSignatureModal() {
    els.modal = q('pdf-sign-modal');
    if (!els.modal) {
        return;
    }
    els.tabs = Array.from(els.modal.querySelectorAll('[data-sign-tab]'));
    els.panes = Array.from(els.modal.querySelectorAll('[data-sign-pane]'));
    els.canvas = q('pdf-sign-canvas');
    els.clearButton = q('pdf-sign-clear');
    els.text = q('pdf-sign-text');
    els.typePreview = q('pdf-sign-type-preview');
    els.fontButtons = Array.from(els.modal.querySelectorAll('[data-sign-font]'));
    els.file = q('pdf-sign-file');
    els.uploadPreview = q('pdf-sign-upload-preview');
    els.savedTab = q('pdf-sign-saved-tab');
    els.savedPreview = q('pdf-sign-saved-preview');
    els.remember = q('pdf-sign-remember');
    els.feedback = q('pdf-sign-feedback');
    els.useButton = q('pdf-sign-use');

    els.tabs.forEach((tab) => tab.addEventListener('click', () => setTab(tab.dataset.signTab)));
    if (els.canvas) {
        bindPad();
    }
    if (els.clearButton) {
        els.clearButton.addEventListener('click', clearPad);
    }
    if (els.text) {
        els.text.addEventListener('input', renderTypedPreview);
        els.text.addEventListener('keydown', (event) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                useSignature().catch(() => {});
            }
        });
    }
    els.fontButtons.forEach((button) => {
        button.addEventListener('click', () => {
            typed.font = button.dataset.signFont;
            renderTypedPreview();
        });
    });
    if (els.file && els.uploadPreview) {
        bindUpload();
    }
    if (els.useButton) {
        els.useButton.addEventListener('click', () => {
            useSignature().catch(() => {
                showAlert('Could not build the signature image.', true);
            });
        });
    }
    ['pdf-sign-close', 'pdf-sign-cancel'].forEach((id) => {
        const button = q(id);
        if (button) {
            button.addEventListener('click', closeSignatureModal);
        }
    });
    els.modal.addEventListener('click', (event) => {
        if (event.target === els.modal) {
            closeSignatureModal();
        }
    });
    syncSavedTab();
    renderTypedPreview();
}
